// src/pages/UrgentAlerts/index.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { listRules, createRule, updateRule, deleteRule, toggleActive } from './UrgentAlerts.api';

export default function UrgentAlertsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [lang, setLang] = useState('all');
  const [active, setActive] = useState('all');

  const [form, setForm] = useState({ phrase: '', type: 'keyword', lang: 'any', score: 2, active: true, notes: '' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  async function fetchData() {
    try {
      setLoading(true);
      setError('');
      const res = await listRules({ q, lang, active });
      setItems(res.items || []);
    } catch (e) {
      setError(e.message || 'Failed to fetch rules.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [q, lang, active]);

  function resetForm() {
    setForm({ phrase: '', type: 'keyword', lang: 'any', score: 2, active: true, notes: '' });
    setEditing(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, score: Number(form.score || 2) };
      if (editing) await updateRule(editing._id, payload);
      else await createRule(payload);
      resetForm();
      fetchData();
    } catch (e2) {
      setError(e2.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this rule?')) return;
    try { await deleteRule(id); fetchData(); }
    catch (e) { setError(e.message || 'Failed to delete.'); }
  }

  async function handleToggle(id, value) {
    try { await toggleActive(id, value); fetchData(); }
    catch (e) { setError(e.message || 'Failed to update.'); }
  }

  const table = useMemo(() => (
    <div className="overflow-auto border rounded-2xl p-2">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-gray-50">
          <tr>
            <th className="text-left p-2">Phrase / Pattern</th>
            <th className="text-left p-2">Type</th>
            <th className="text-left p-2">Lang</th>
            <th className="text-left p-2">Score</th>
            <th className="text-left p-2">Active</th>
            <th className="text-left p-2">Updated</th>
            <th className="text-left p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={it._id} className="border-t">
              <td className="p-2">
                <div className="font-medium">{it.phrase}</div>
                {it.notes ? <div className="text-xs text-gray-500">{it.notes}</div> : null}
              </td>
              <td className="p-2">{it.type}</td>
              <td className="p-2">{it.lang}</td>
              <td className="p-2">{it.score}</td>
              <td className="p-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!it.active} onChange={e => handleToggle(it._id, e.target.checked)} />
                  <span>{it.active ? 'On' : 'Off'}</span>
                </label>
              </td>
              <td className="p-2">{new Date(it.updatedAt).toLocaleString()}</td>
              <td className="p-2 flex gap-2">
                <button
                  className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200"
                  onClick={() => {
                    setEditing(it);
                    setForm({
                      phrase: it.phrase,
                      type: it.type,
                      lang: it.lang,
                      score: it.score,
                      active: it.active,
                      notes: it.notes || ''
                    });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}>
                  Edit
                </button>
                <button
                  className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                  onClick={() => handleDelete(it._id)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {!items.length && !loading ? (
            <tr><td className="p-4 text-center text-gray-500" colSpan={7}>No rules found.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  ), [items, loading]);

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Urgent Alerts</h1>
      </div>

      {/* Short banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-600">
        <p>
          Configure <strong>Urgent Alert Rules</strong>. When a message matches these keywords or regex patterns,
          the system escalates it as urgent, notifies the team and skips AI auto‑reply.
        </p>
      </div>

      {/* Collapsible detailed explanation */}
      <details className="border rounded-lg p-3 bg-gray-50 text-sm">
        <summary className="cursor-pointer font-medium select-none">
          How it works (detailed)
        </summary>
        <div className="mt-3 space-y-3 text-gray-700">
          <p>
            <strong>Phrase / Pattern</strong>: the word or expression to look for in customer messages.
            Examples: <code>urgent</code>, <code>emergência</code>, <code>accidente</code>.
            If <em>Type</em> is <code>regex</code>, provide a regular expression (e.g. <code>\\bdeadline\\b.*\\btoday\\b</code>).
          </p>
          <p>
            <strong>Type</strong>:
            <br/>• <code>keyword</code> – the message is normalized (lowercase, accents removed) and we check if it contains the phrase.
            <br/>• <code>regex</code> – the regular expression is executed on the raw text (powerful, use carefully).
          </p>
          <p>
            <strong>Language</strong>: limit the rule to a detected language (<code>pt</code>, <code>es</code>, <code>en</code>)
            or select <code>any</code> to apply it to all. The AIR detects language before evaluating the rules.
          </p>
          <p>
            <strong>Score</strong>: each matched rule contributes its score. The AIR classifies a message as
            <em> urgent </em> when the total score is <strong>≥ 2</strong>. Examples:
            <br/>• one rule with score 2 (e.g., <code>urgent</code>) → triggers alone;
            <br/>• two rules with score 1 each (e.g., <code>help</code> + <code>now</code>) → combined trigger.
            <br/>Exclamation marks may add a small boost.
          </p>
          <p>
            <strong>Active</strong>: when Off, the rule is kept for reference but ignored by detection.
          </p>
          <p>
            <strong>Flow</strong> when an urgent message is detected:
            <br/>1) The system stores the message with <code>meta.urgent = true</code> and <code>awaitingHuman = true</code>;
            <br/>2) Notifies all <em>Alert Recipients</em> via WhatsApp;
            <br/>3) Sends a brief acknowledgment to the customer (no AI auto‑reply).
          </p>
        </div>
      </details>

      {/* Form */}
      <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-12 items-end border rounded-2xl p-4">
        <div className="md:col-span-5">
          <label className="block text-sm font-medium mb-1">Phrase / Pattern</label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={form.phrase}
            onChange={e => setForm(f => ({ ...f, phrase: e.target.value }))}
            placeholder={form.type === 'regex' ? 'Regex (e.g., \\bdeadline\\b.*\\btoday\\b)' : 'Keyword (e.g., urgent, emergência)'}
            required
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Type</label>
          <select
            className="w-full border rounded px-3 py-2 text-sm"
            value={form.type}
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            <option value="keyword">keyword</option>
            <option value="regex">regex</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Language</label>
          <select
            className="w-full border rounded px-3 py-2 text-sm"
            value={form.lang}
            onChange={e => setForm(f => ({ ...f, lang: e.target.value }))}>
            <option value="any">any</option>
            <option value="pt">pt</option>
            <option value="es">es</option>
            <option value="en">en</option>
          </select>
        </div>
        <div className="md:col-span-1">
          <label className="block text-sm font-medium mb-1">Score</label>
          <input
            type="number" min={1} max={10}
            className="w-full border rounded px-3 py-2 text-sm"
            value={form.score}
            onChange={e => setForm(f => ({ ...f, score: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Active</label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.active}
              onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
            />
            <span className="text-sm">{form.active ? 'On' : 'Off'}</span>
          </label>
        </div>
        <div className="md:col-span-12">
          <label className="block text-sm font-medium mb-1">Notes</label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Optional note for this rule"
          />
        </div>
        <div className="md:col-span-12 flex gap-2">
          <button className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 text-sm" disabled={saving}>
            {editing ? 'Update Rule' : 'Add Rule'}
          </button>
          {editing && (
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
              onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading ? <div className="text-gray-500">Loading...</div> : table}
    </div>
  );
}
